import { isOpen, type Appointment } from "@so/shared";
import { Link } from "react-router-dom";
import { useAppointments, useMe } from "../api/queries.js";
import { AppointmentTime, SpecialtyBadge, StatusBadge } from "../components/appointment.js";
import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from "../components/ui.js";

function ConsultationRow({
  appointment,
  viewerTimezone,
  isPatient,
}: {
  readonly appointment: Appointment;
  readonly viewerTimezone: string;
  readonly isPatient: boolean;
}) {
  const counterpart = isPatient ? appointment.clinician_name : appointment.patient_name;

  return (
    <Card as="li" className="hover:border-line-strong transition-colors">
      <Link
        to={`/consultations/${appointment.appointment_id}`}
        className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4"
      >
        <div className="min-w-[240px] flex-1">
          <p className="text-[15.5px] font-medium">{counterpart}</p>
          <div className="mt-1.5">
            <AppointmentTime
              appointment={appointment}
              viewerTimezone={viewerTimezone}
              showCounterpart
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <SpecialtyBadge specialty={appointment.specialty} />
          <StatusBadge status={appointment.status} />
        </div>
      </Link>
    </Card>
  );
}

export function Consultations() {
  const { data: me } = useMe();
  const { data: appointments, isPending, error } = useAppointments();

  if (isPending) return <Loading label="Loading your consultations" />;
  if (error) return <ErrorNote error={error} />;

  const viewerTimezone = me?.timezone ?? "UTC";
  const isPatient = me?.roles.includes("patient") === true && me.roles.includes("clinician") !== true;
  const isCoordinator = me?.roles.includes("admin") === true;

  // Anything still needing attention first, then the finished ones. Sorting is
  // otherwise by time, which buries a cancelled consultation somebody has to
  // deal with underneath next month's calendar.
  const active = appointments.filter((a) => isOpen(a.status));
  const closed = appointments.filter((a) => !isOpen(a.status));

  return (
    <>
      <PageHeader
        eyebrow="Consultations"
        title={isPatient ? "Your consultations" : "Your caseload"}
        actions={
          isPatient ? (
            <Link to="/specialists">
              <Button>Find a specialist</Button>
            </Link>
          ) : undefined
        }
      >
        {isPatient
          ? "Everything you have booked, and the written opinions you have received."
          : "Consultations assigned to you, soonest first."}
      </PageHeader>

      {appointments.length === 0 ? (
        <EmptyState
          title={
            isPatient
              ? "You have no consultations yet"
              : isCoordinator
                ? "You are not a party to any consultation"
                : "Nothing is assigned to you yet"
          }
          action={
            isPatient ? (
              <Link to="/specialists">
                <Button>Browse specialists</Button>
              </Link>
            ) : isCoordinator ? (
              <Link to="/queue">
                <Button>Open the coordination queue</Button>
              </Link>
            ) : undefined
          }
        >
          {isPatient
            ? "Choose a specialist in the relevant field, pick a time that suits you, and upload the records you would like reviewed."
            : isCoordinator
              ? "This page shows only consultations you are personally a party to. Everything across all patients lives in the coordination queue."
              : "New second-opinion requests will appear here, and you will be emailed when one arrives."}
        </EmptyState>
      ) : (
        <div className="space-y-8">
          {active.length > 0 && (
            <section>
              <h2 className="text-ink-soft mb-3 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
                Upcoming and in progress
              </h2>
              <ul className="space-y-2.5">
                {active.map((appointment) => (
                  <ConsultationRow
                    key={appointment.appointment_id}
                    appointment={appointment}
                    viewerTimezone={viewerTimezone}
                    isPatient={isPatient}
                  />
                ))}
              </ul>
            </section>
          )}

          {closed.length > 0 && (
            <section>
              <h2 className="text-ink-soft mb-3 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
                Completed and cancelled
              </h2>
              <ul className="space-y-2.5">
                {closed.map((appointment) => (
                  <ConsultationRow
                    key={appointment.appointment_id}
                    appointment={appointment}
                    viewerTimezone={viewerTimezone}
                    isPatient={isPatient}
                  />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </>
  );
}
